import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, Hammer, Heart, ArrowRight, Play } from "lucide-react";
import coachImg from "@/assets/mockups/coach-portfolio.jpg";
import artisanImg from "@/assets/mockups/artisan-portfolio.jpg";
import consultantImg from "@/assets/mockups/consultant-portfolio.jpg";

const sectors = [
  {
    id: "coach",
    label: "Coach / Thérapeute",
    icon: Heart,
    image: coachImg,
    description: "Design chaleureux, sections témoignages, prise de rendez-vous et offres de coaching mises en avant.",
    features: ["Prise de RDV intégrée", "Témoignages clients", "Tarifs & Offres"],
    color: "from-amber-500 to-orange-400",
  },
  {
    id: "artisan",
    label: "Artisan / Créateur",
    icon: Hammer,
    image: artisanImg,
    description: "Galerie de réalisations immersive, palette authentique et formulaire de devis pour vos créations sur-mesure.",
    features: ["Galerie photos HD", "Devis en ligne", "Storytelling métier"],
    color: "from-emerald-500 to-teal-400",
  },
  {
    id: "consultant",
    label: "Consultant / Expert",
    icon: Briefcase,
    image: consultantImg,
    description: "Design corporate, études de cas avec KPIs, logos clients et positionnement expert clairement affiché.",
    features: ["Études de cas & KPIs", "Logos clients", "Positionnement expert"],
    color: "from-blue-500 to-indigo-400",
  },
];

const SellGallery = () => {
  const [active, setActive] = useState(0);

  return (
    <section className="py-24 scroll-mt-20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Exemples par secteur
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Un design adapté à{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              votre métier
            </span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Chaque portfolio est pensé pour votre secteur d'activité. Découvrez quelques exemples.
          </p>
        </motion.div>

        {/* Sector Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {sectors.map((sector, i) => (
            <motion.button
              key={sector.id}
              onClick={() => setActive(i)}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`flex items-center gap-2.5 px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 border ${
                active === i
                  ? "bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/25"
                  : "bg-card text-muted-foreground border-border/50 hover:border-primary/40 hover:text-foreground"
              }`}
            >
              <sector.icon className="w-4 h-4" />
              {sector.label}
            </motion.button>
          ))}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="max-w-6xl mx-auto"
          >
            <div className="grid lg:grid-cols-5 gap-8 items-center">
              {/* Mockup Screenshot */}
              <div className="lg:col-span-3 relative">
                <div className="rounded-2xl border border-border/50 overflow-hidden shadow-2xl bg-card">
                  {/* Browser chrome */}
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-secondary/50 border-b border-border/30">
                    <div className="w-3 h-3 rounded-full bg-destructive/50" />
                    <div className="w-3 h-3 rounded-full bg-enterprise-warning/50" />
                    <div className="w-3 h-3 rounded-full bg-enterprise-success/50" />
                    <div className="flex-1 mx-3">
                      <div className="h-5 rounded-md bg-background/60 flex items-center px-3">
                        <span className="text-xs text-muted-foreground">
                          🔒 {sectors[active].id}-portfolio.com
                        </span>
                      </div>
                    </div>
                  </div>
                  <img
                    src={sectors[active].image}
                    alt={`Portfolio ${sectors[active].label}`}
                    className="w-full aspect-[4/3] object-cover object-top"
                  />
                </div>
                {/* Floating badge */}
                <motion.div
                  className={`absolute -bottom-3 -right-3 px-4 py-2 rounded-xl bg-gradient-to-r ${sectors[active].color} text-white text-xs font-bold shadow-lg`}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ repeat: Infinity, duration: 2.5 }}
                >
                  100% personnalisable
                </motion.div>
              </div>

              {/* Info panel */}
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r ${sectors[active].color} text-white text-xs font-semibold mb-3`}>
                    {(() => { const Icon = sectors[active].icon; return <Icon className="w-3.5 h-3.5" />; })()}
                    {sectors[active].label}
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{sectors[active].label}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {sectors[active].description}
                  </p>
                </div>

                {/* Feature list */}
                <div className="space-y-3">
                  {sectors[active].features.map((feature, i) => (
                    <motion.div
                      key={feature}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border/30"
                    >
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${sectors[active].color} flex items-center justify-center flex-shrink-0`}>
                        <ArrowRight className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                <a href="#contact">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className={`w-full mt-2 px-6 py-3.5 rounded-xl bg-gradient-to-r ${sectors[active].color} text-white font-semibold text-sm shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center gap-2`}
                  >
                    Je veux ce style
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </a>

                <a href="/" target="_blank" rel="noopener">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="w-full px-6 py-3 rounded-xl border border-primary/40 text-primary font-semibold text-sm hover:bg-primary/10 transition-colors flex items-center justify-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Voir la démo live
                  </motion.button>
                </a>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SellGallery;
